import SectionHeader from '@components/common/section-header'

import ProductCardAlpine from '@components/product/product-cards/product-card-alpine'
import ProductCardNew from './product-cards/product-card-new'

import { Product } from '@framework/types'
import Carousel from '@components/ui/carousel/carousel'
import { SwiperSlide } from '@components/ui/carousel/slider'
import Alert from '@components/ui/alert'
import SeeAll from '@components/ui/see-all'
import useWindowSize from '@utils/use-window-size'
import ProductCardLoader from '@components/ui/loaders/product-card-loader'
import cn from 'classnames'
import { useRouter } from 'next/router'
import { getDirection } from '@utils/get-direction'
import ProductCardMightLike from './product-cards/product-card-mightlike'
import { FC } from 'react'

import sassStyles from '@assets/css/modules/products-carousel-new.module.scss'

interface ProductsCarouselProps {
  sectionHeading: string
  categorySlug?: string
  className?: string
  products?: Product[]
  loading: boolean
  error?: string
  limit?: number
  uniqueKey?: string
  carouselBreakpoint?: {} | any
  titleClassName?: string
}

const breakpoints = {
  '1921': {
    slidesPerView: 7,
  },
  '1780': {
    slidesPerView: 7,
  },
  '1536': {
    slidesPerView: 6,
  },
  '1280': {
    slidesPerView: 5,
  },
  '1024': {
    slidesPerView: 4,
  },
  '640': {
    slidesPerView: 3,
  },
  '360': {
    slidesPerView: 2,
  },
  '0': {
    slidesPerView: 1,
  },
}

const ProductsCarousel: FC<ProductsCarouselProps> = ({
  sectionHeading,
  categorySlug,
  className = 'mb-8 lg:mb-10 xl:mb-12',
  products,
  loading,
  error,
  limit,
  uniqueKey,
  carouselBreakpoint,
  titleClassName = 'mb-0',
}) => {
  const { width } = useWindowSize()
  const { locale } = useRouter()
  const dir = getDirection(locale)
  return (
    <div
      className={cn(
        'max-w-[1920px] overflow-hidden 4xl:overflow-visible px-4 2xl:ltr:pl-10 2xl:rtl:pr-10 2xl:ltr:pr-0 2xl:rtl:pl-0 4xl:ltr:pr-10 4xl:rtl:pl-10 mx-auto relative',
        className,
      )}
      // style={{ paddingLeft: '0px' }}
    >
      <div className="flex flex-wrap items-center justify-between mb-5 md:mb-6">
        <SectionHeader
          sectionHeading={sectionHeading}
          className={titleClassName}
        />
      </div>
      {error ? (
        <div className="2xl:ltr:pr-10 2xl:rtl:pl-10">
          <Alert message={error} />
        </div>
      ) : (
        <>
          <div className="hidden lg:block">
            <div
              // after-item-opacity (for right shadow)
              className={`heightFull w-full relative  ${
                dir === 'rtl'
                  ? 'xl:-ml-40 2xl:-ml-28 4xl:ml-0'
                  : 'xl:-mr-40 2xl:-mr-28 4xl:mr-0'
              }`}
            >
              <Carousel
                breakpoints={carouselBreakpoint || breakpoints}
                className=" -mt-4 "
                swiperClass="shadow-bestSeller "
                prevButtonClassName="ltr:-left-2 rtl:-right-2 md:ltr:-left-1 md:rtl:-right-1.5 lg:ltr:-left-2 rtl:-right-2 xl:ltr:-left-2.5 xl:rtl:-right-2.5 2xl:ltr:left-5 2xl:rtl:right-5 -top-12 3xl:top-auto 3xl:-translate-y-2 4xl:-translate-y-10"
                nextButtonClassName="xl:rtl:-translate-x-2.5 xl:lrt:translate-x-2.5 ltr:-right-2 rtl:-left-2  xl:rtl:left-40 -top-12 3xl:top-auto transform 2xl:translate-x-0 3xl:-translate-y-2 4xl:-translate-y-10"
              >
                {loading && !products?.length ? (
                  Array.from({ length: limit! }).map((_, idx) => (
                    <SwiperSlide
                      key={`${uniqueKey}-${idx}`}
                      className={`${sassStyles['product-card-new-slide']} px-1.5 md:px-2 xl:px-2.5 py-4`}
                      style={{ minWidth: '348px' }}
                    >
                      <ProductCardLoader uniqueKey={`${uniqueKey}-${idx}`} />
                    </SwiperSlide>
                  ))
                ) : (
                  <>
                    {products?.map((product: any, idx) =>
                      sectionHeading === 'text-related-products' &&
                      !product?.is_in_stock ? null : (
                        <SwiperSlide
                          key={`${uniqueKey}-${idx}`}
                          className={`${sassStyles['product-card-new-slide']} px-1.5 md:px-2 xl:px-2.5 py-4`}
                          // style={{ minWidth: '348px' }}
                        >
                          <div
                            key={idx}
                            className="relative borobazar-alpine-product-card rounded-[10px] bg-white-- transition-all hover:shadow-product-hover shadow-bestSeller bg-white"
                          >
                            <ProductCardNew product={product} />
                          </div>
                        </SwiperSlide>
                      ),
                    )}
                    <SwiperSlide className="p-2.5 flex items-center justify-center">
                      {/* <SeeAll href={categorySlug} /> @TODO put this back? */}
                    </SwiperSlide>
                    {width! > 1024 && width! < 1921 && <SwiperSlide />}
                  </>
                )}
              </Carousel>
            </div>
          </div>
          <div className="block lg:hidden">
            <div className="gs-product-grid-main-wrap gs-grid-item-wrap gs-maple-grid-wrap grid grid-cols-2 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 1xl:grid-cols-5 2xl:grid-cols-5 4xl:grid-cols-6 gap-3 md:gap-4 2xl:gap-5">
              {products?.map((product: any, index: any) => (
                <div
                  key={index}
                  className="relative borobazar-alpine-product-card rounded-[10px] bg-white-- transition-all hover:shadow-product-hover shadow-bestSeller bg-white"
                >
                  <ProductCardNew product={product} />
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default ProductsCarousel
